import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, Link, useNavigate } from 'react-router-dom';
import { useClient } from '../connect_api';
import { Customer, ProductService, TrackableItem, TrackableItemState } from '../api/api_pb';
import RelativeTime from '../components/RelativeTime';
import { toast } from 'react-toastify';
import CurrencyFormatter from '../utils/currencyFormatter';
import CreateTrackableItemModal from '../components/CreateTrackableItemModal';
import StateEditModal from '../components/StateEditModal';
import { useGlobalContext } from '../GlobalContext';

const Stocks: React.FC = () => {
    const { uuid } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { findProductByUuid: findProductById } = useGlobalContext();
    const product = location.state?.product || findProductById(uuid!);
    const [stockItems, setStockItems] = useState<TrackableItem[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [showModal, setShowModal] = useState(false);
    const [currentState, setCurrentState] = useState<TrackableItemState>(TrackableItemState.READY);
    const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [currentName, setCurrentName] = useState<string>('');
    const { updateCustomers } = useGlobalContext();


    const productClient = useClient(ProductService);

    const fetchStockData = useCallback(async () => {
        setIsLoading(true);

        try {
            const timeoutId = setTimeout(() => {
                toast.dismiss();
                toast.error('Request timed out');
            }, 10000);

            try {
                const response = await productClient.listProductTrackableItems({
                    productUuid: product.uuid,
                });
                clearTimeout(timeoutId);

                if (response.trackableItems.length === 0) {
                } else {
                    setStockItems(response.trackableItems);
                }
            } catch (e) {
                clearTimeout(timeoutId);
                toast.dismiss();
                toast.error('Failed to fetch data');
                console.log('error', e);
            }
        } catch (err) {
            toast.dismiss();
            toast.error('Failed to fetch data');
            console.log('error', err);
        } finally {
            setIsLoading(false);
        }
    }, [productClient, product.uuid]);

    useEffect(() => {
        fetchStockData();
    }, [uuid, fetchStockData]);

    const handleUpdateState = async (newState: TrackableItemState, newName: string) => {
        if (selectedItemId) {
            try {
                await productClient.updateTrackableItem({
                    name: newName,
                    trackableItemUuid: selectedItemId,
                    state: newState,
                });
                toast.dismiss();
                toast.success('Update TrackableItem Successfully');
                fetchStockData();
            } catch (err) {
                toast.dismiss();
                toast.error('Failed to Update Trackable Item');
                console.error('Update TrackableItem Error', err);
            }
        }
    };

    const openStateModal = (item: TrackableItem) => {
        setCurrentState(item.state || TrackableItemState.READY);
        setCurrentName(item.name);
        setSelectedItemId(item.uuid);
        setShowModal(true);
    };

    const handleShowleasesClick = (customer: Customer | undefined) => {
        if (customer) {
            updateCustomers(customer);
            navigate(`/show-lease/${customer.uuid}`);
        }
    };

    return (
        <div className="p-4">
            {isLoading ? (
                <div className="text-center">Loading...</div>
            ) : (
                <>
                    {product && (
                        <div className="mb-8">
                            <table className="w-full border-collapse border border-gray-200 mb-4">
                                <thead>
                                    <tr className="bg-gray-100">
                                        <th className="border text-left border-gray-300 p-2">UUID</th>
                                        <th className="border text-left border-gray-300 p-2">Name</th>
                                        <th className="border text-left border-gray-300 p-2">Image URL</th>
                                        <th className="border text-left border-gray-300 p-2">Price</th>
                                        <th className="border text-left border-gray-300 p-2">For Sale</th>
                                        <th className="border text-left border-gray-300 p-2">Created At</th>
                                        <th className="border text-left border-gray-300 p-2">Updated At</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td className="border border-gray-300 p-2">{product.uuid}</td>
                                        <td className="border border-gray-300 p-2">{product.name}</td>
                                        <td className="border border-gray-300 p-2">
                                            <img src={product.imageUrl} alt={product.name}
                                                className="w-16 h-16 object-cover" />
                                        </td>
                                        <td className="border border-gray-300 p-2"><CurrencyFormatter
                                            priceInCents={product.priceInCents} /></td>
                                        <td className="border border-gray-300 p-2">{product.forSale ? "yes" : "no"}</td>
                                        <td className="border border-gray-300 p-2">
                                            {product.createdAt ? (
                                                <div className="text-sm">
                                                    <div><RelativeTime timestamp={product.createdAt} /></div>
                                                </div>
                                            ) : ''}
                                        </td>
                                        <td className="border border-gray-300 p-2">
                                            {product.updatedAt ? (
                                                <div className="text-sm">
                                                    <div><RelativeTime timestamp={product.updatedAt} /></div>
                                                </div>
                                            ) : ''}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    )}
                    <div className="flex justify-end items-center mb-4">
                        <button onClick={() => setShowCreateModal(true)} className="bg-green-500 text-white px-4 py-2 rounded mb-4">
                            Create Trackable Item
                        </button>
                    </div>

                    <table className="w-full border-collapse border border-gray-200">
                        <thead>
                            <tr className="bg-gray-100">
                                <th className="border text-left border-gray-300 p-2">UUID</th>
                                <th className="border text-left border-gray-300 p-2">Name</th>
                                <th className="border text-left border-gray-300 p-2">State</th>
                                <th className="border text-left border-gray-300 p-2">Location</th>
                                <th className="border text-left border-gray-300 p-2">InUseCustomer</th>
                                <th className="border text-left border-gray-300 p-2">Created At</th>
                                <th className="border text-left border-gray-300 p-2">Updated At</th>
                                <th className="border p-2">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {stockItems.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="border border-gray-300 p-2 text-center">No data available</td>
                                </tr>
                            ) : (
                                stockItems.map(item => (
                                    <tr key={item.uuid}>
                                        <td className="border border-gray-300 p-2">{item.uuid}</td>
                                        <td className="border border-gray-300 p-2">{item.name}</td>
                                        <td className="border border-gray-300 p-2">
                                            <div> {TrackableItemState[item.state]}</div>
                                        </td>
                                        <td className="border border-gray-300 p-2">
                                            {item.location?.machineName}<br />
                                            {item.location?.doorIdDisplay}
                                        </td>
                                        <td className="border border-gray-300 px-2 py-2 text-blue-500 hover:underline cursor-pointer">
                                            {/* <CustomerLink inUseCustomer={item.inUseCustomer} inUseSinceAt={item.inUseSinceAt} /> */}
                                            <Link
                                                to={`/show-lease/${item.inUseCustomer?.uuid}`}
                                                onClick={() => handleShowleasesClick(item.inUseCustomer)}
                                                className="text-blue-500 hover:underline"
                                            >
                                                {item.inUseCustomer?.mobileNumber}
                                            </Link>
                                            {item.inUseSinceAt ? (
                                                <div className="text-sm text-black">
                                                    <div><RelativeTime timestamp={item.inUseSinceAt} /></div>
                                                </div>
                                            ) : ''}
                                        </td>
                                        <td className="border border-gray-300 p-2">
                                            {item.createdAt ? (
                                                <div className="text-sm">
                                                    <div><RelativeTime timestamp={item.createdAt} /></div>
                                                </div>
                                            ) : ''}
                                        </td>
                                        <td className="border border-gray-300 p-2">
                                            {item.updatedAt ? (
                                                <div className="text-sm">
                                                    <div><RelativeTime timestamp={item.updatedAt} /></div>
                                                </div>
                                            ) : ''}
                                        </td>
                                        <td className="border p-2">
                                            <button
                                                className="btn btn-primary mt-3"
                                                onClick={() => openStateModal(item)}
                                            >
                                                Edit Trackable Item
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </>
            )}

            <CreateTrackableItemModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                productUuid={product.uuid}
                onStockCreated={fetchStockData}
            />

            {/* State Update Modal */}
            <StateEditModal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                currentState={currentState}
                currentName={currentName}
                onUpdate={handleUpdateState}
            />
        </div>
    );
};

export default Stocks;
